import { useState } from "react";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import { Button } from "@axismaxlife/unified-ui-components-library";
import { ImageLibrary } from "../constants/ImageLibrary";
import { String } from "../constants/Strings";
import { colors } from "../style/color/colors";
import { fonts } from "../style/fonts/fonts";
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";

const MONTH_NAMES = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const formatSummaryDate = (val) => {
    if (!val) return val;
    if (val instanceof Date && !isNaN(val.getTime())) {
        return `${val.getDate()} ${MONTH_NAMES[val.getMonth()]}, ${val.getFullYear()}`;
    }
    if (typeof val !== "string") return val;
    const str = val.trim();

    // Check DD-MM-YYYY or DD/MM/YYYY
    const dmyMatch = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dmyMatch) {
        const day = parseInt(dmyMatch[1], 10);
        const month = parseInt(dmyMatch[2], 10);
        const year = parseInt(dmyMatch[3], 10);
        if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
            return `${day} ${MONTH_NAMES[month - 1]}, ${year}`;
        }
    }

    // Check YYYY-MM-DD or YYYY/MM/DD (with optional ISO time)
    const ymdMatch = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:T.*)?$/);
    if (ymdMatch) {
        const year = parseInt(ymdMatch[1], 10);
        const month = parseInt(ymdMatch[2], 10);
        const day = parseInt(ymdMatch[3], 10);
        if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
            return `${day} ${MONTH_NAMES[month - 1]}, ${year}`;
        }
    }

    return val;
};

const Cell = ({ label, value }) => {
    if (!label && !value) return null;
    return (
        <Box sx={{ textAlign: "left" }}>
            <Typography
                sx={{
                    fontSize: "13px",
                    fontFamily: fonts.notoSans,
                    color: colors.NeutralDark600,
                    fontWeight: 400,
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    mt: "2px",
                    fontSize: "16px",
                    fontFamily: fonts.notoSans,
                    color: colors.PrimaryWoodSmoke,
                    fontWeight: 400,
                }}
            >
                {formatSummaryDate(value)}
            </Typography>
        </Box>
    );
};

export const DetailSummaryCard = ({
    title,
    subtitle,
    rows = [],
    onEdit,
    onDelete,
}) => {
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const handleDeleteClick = () => {
        setIsConfirmOpen(true);
    };

    const handleConfirmDelete = () => {
        setIsConfirmOpen(false);
        if (onDelete) {
            onDelete();
        }
    };

    const resolveCellLabel = (label) => {
        if (!label) return label;
        const normalized = `${label}`.trim().toLowerCase();
        if (normalized === "total amount") {
            const normalizedTitle = `${title}`.trim().toUpperCase();
            if (normalizedTitle.includes("EPF")) return "EPF amount";
            if (normalizedTitle.includes("PPF")) return "PPF amount";
        }
        return label;
    };

    const premiumRow = !subtitle
        ? rows.find(
            (r) =>
                r?.leftLabel &&
                `${r.leftLabel}`.trim().toLowerCase().includes("yearly premium")
        )
        : null;
    const displaySubtitle =
        subtitle ||
        (premiumRow && premiumRow.leftValue
            ? `${premiumRow.leftLabel} : ${premiumRow.leftValue}`
            : null);
    const visibleRows = premiumRow ? rows.filter((r) => r !== premiumRow) : rows;

    return (
        <>
            <Box
                sx={{
                    p: "16px",
                    textAlign: "left",
                    borderRadius: "12px",
                    border: `1px solid ${colors.softGray}`,
                    backgroundColor: colors.PrimaryWhite,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: "12px",
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontSize: "16px",
                                fontFamily: fonts.notoSans,
                                fontWeight: 500,
                                color: colors.PrimaryWoodSmoke,
                            }}
                        >
                            {title}
                        </Typography>
                        {displaySubtitle && (
                            <Typography
                                sx={{
                                    mt: "4px",
                                    fontSize: "13px",
                                    fontFamily: fonts.notoSans,
                                    color: colors.NeutralDark600,
                                    fontWeight: 400,
                                }}
                            >
                                {typeof displaySubtitle === "string" && displaySubtitle.includes(" : ") ? (
                                    <>
                                        {displaySubtitle.split(" : ")[0]} :{" "}
                                        <Box
                                            component="span"
                                            sx={{
                                                color: colors.PrimaryWoodSmoke,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {displaySubtitle.split(" : ").slice(1).join(" : ")}
                                        </Box>
                                    </>
                                ) : (
                                    displaySubtitle
                                )}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: "flex", gap: "8px", flexShrink: 0, mt: "-2px" }}>
                        <IconButton
                            sx={{ p: "4px" }}
                            onClick={handleDeleteClick}
                            aria-label={`${String.ChildPlannig.deleteLabel} ${title}`}
                        >
                            <Box
                                component="img"
                                src={ImageLibrary.TRASH}
                                alt=""
                                aria-hidden="true"
                                sx={{ display: "block", width: "25px", height: "auto" }}
                            />
                        </IconButton>
                        <IconButton
                            sx={{ p: "4px" }}
                            onClick={onEdit}
                            aria-label={`${String.ChildPlannig.editLabel} ${title}`}
                        >
                            <Box
                                component="img"
                                src={ImageLibrary.EDIT}
                                alt=""
                                aria-hidden="true"
                                sx={{ display: "block", width: "25px", height: "auto" }}
                            />
                        </IconButton>
                    </Box>
                </Box>

                {visibleRows.map((row, index) => (
                    <Box key={index}>
                        <Divider
                            sx={{
                                my: "12px",
                                borderStyle: index === 0 ? "solid" : "dashed",
                                borderColor: colors.NeutralLightN60,
                            }}
                        />
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: "16px",
                            }}
                        >
                            <Cell label={resolveCellLabel(row.leftLabel)} value={row.leftValue} />
                            <Cell label={resolveCellLabel(row.rightLabel)} value={row.rightValue} />
                        </Box>
                    </Box>
                ))}
            </Box>

            <DeleteConfirmationModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={handleConfirmDelete}
            />
        </>
    );
};

export const AddAnotherLink = ({ label, onClick }) => (
    <Box sx={{ display: "inline-flex", justifyContent: "center" }}>
        <Button
            buttonType="neutralLink"
            onClick={onClick}
            iconLeft={
                <Box
                    component="img"
                    src={ImageLibrary.PLUS}
                    alt=""
                    aria-hidden="true"
                    sx={{ display: "block", width: "16px", height: "auto" }}
                />
            }
        >
            {label}
        </Button>
    </Box>
);
